import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@/lib/ui-tokens';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';

/**
 * P13-02 client side of the semantic layer.
 *
 * Every panel on this screen is a `{ measures, dimensions, filters }` selection
 * against `POST /analytics/query` - there is no per-report endpoint to call, so
 * a new panel is a new query object rather than new plumbing.
 */

export type SemanticValue = string | number | null;
export type SemanticRow = Record<string, SemanticValue>;

export interface AnalyticsQuery {
  measures: string[];
  dimensions?: string[];
  filters?: Record<string, string | undefined>;
  dateRange?: { from?: string; to?: string };
  limit?: number;
}

export interface AnalyticsResult {
  measures: string[];
  dimensions: string[];
  dateRange: { from: string; to: string };
  timezone: string;
  rows: SemanticRow[];
  rowCount: number;
  truncated: boolean;
}

export interface KpiResult {
  dateRange: { from: string; to: string };
  branchId: string | null;
  commercial: SemanticRow;
  operational: SemanticRow;
  fulfillment: SemanticRow[];
}

export interface DashboardResult {
  timezone: string;
  since: string;
  live: {
    openTables: number;
    activeOrders: number;
    runningTimers: number;
    openServiceRequests: number;
  };
  today: SemanticRow;
  hourly: SemanticRow[];
  kitchen: { openItems: number; awaitingPickup: number; slaBreached: number };
}

export interface HeatmapElement {
  mapElementId: string;
  entityType: string | null;
  entityId: string | null;
  label: string | null;
  value: string | number | null;
  intensity: number | null;
}

export interface HeatmapResult {
  floorId: string;
  floorName: string;
  metric: 'revenue' | 'occupancy' | 'service';
  unit: 'money' | 'ratio' | 'seconds';
  higherIsBetter: boolean;
  max: string | number;
  elements: HeatmapElement[];
}

/**
 * `useApi` covers GET; the semantic endpoint is a POST because a report is a
 * body, not a query string. Same `{ data, loading, failed, reload }` shape so
 * the panels read identically either way.
 */
export function useAnalyticsQuery<T = AnalyticsResult>(body: AnalyticsQuery | null) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; failed: boolean }>({
    data: null,
    loading: !!body,
    failed: false,
  });

  const key = JSON.stringify(body ?? null);

  const load = useCallback(() => {
    if (!body) {
      setState({ data: null, loading: false, failed: false });
      return;
    }
    setState((s) => ({ ...s, loading: true, failed: false }));
    api
      .post('/analytics/query', body)
      .then(({ data }) => setState({ data: data as T, loading: false, failed: false }))
      .catch(() => setState({ data: null, loading: false, failed: true }));
    // `body` is compared by value through `key`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(load, [load]);

  return { ...state, reload: load };
}

export interface BranchOption {
  id: string;
  name: string;
  currency: string;
}

/** The branch filter, shared by Reports and Overview. */
export function useBranchOptions() {
  const { data, loading } = useApi<unknown>('/branches', { limit: 100 });
  const branches = useMemo(() => rowsOf<BranchOption>(data).rows, [data]);
  return { branches, loading };
}

export interface ScreenCurrency {
  /** The one currency every figure on screen is in, or null when they differ. */
  code: string | null;
  /** True when a tenant-wide figure spans more than one currency. */
  mixed: boolean;
  /** The distinct codes in play, for the "spans SAR, KWD" note. */
  codes: string[];
}

/**
 * D-02 — which currency the figures on this screen are denominated in.
 *
 * With a branch selected the answer is that branch's currency and there is
 * nothing to decide. With "All branches" the API returns one tenant-wide sum,
 * and the old code stamped it with `branches[0].currency` — so a chain running
 * Kuwait and Riyadh showed a part-dinar figure labelled "SAR". That is a
 * plausible-looking wrong number, which is worse than an obviously incomplete
 * one.
 *
 * The semantic layer has no per-currency breakdown to group by (there is no
 * `currency` dimension), so the honest answer is the second one the audit
 * allows: return `code: null`, which makes `useFormat().money()` render the
 * figure as a plain number with no currency label, and let the screen say
 * out loud that it spans several. Picking a branch restores a real money figure.
 *
 * A single-currency chain is unaffected: every branch agrees, so the tenant-wide
 * sum is legitimately in that currency.
 */
export function screenCurrency(
  branches: BranchOption[],
  branchId: string,
  fallback: string,
): ScreenCurrency {
  const selected = branches.find((branch) => branch.id === branchId);
  if (selected?.currency)
    return { code: selected.currency, mixed: false, codes: [selected.currency] };

  const codes = Array.from(
    new Set(branches.map((branch) => branch.currency).filter((code): code is string => !!code)),
  );
  if (codes.length > 1) return { code: null, mixed: true, codes };
  return { code: codes[0] ?? fallback, mixed: false, codes: codes.length ? codes : [fallback] };
}

// --------------------------------------------------------------- formatting

const MINUTE = 60;
const HOUR = 3600;

/**
 * §25 - every number is formatted through the active locale, so Arabic gets
 * Arabic digits and its own separators without a second code path.
 */
/**
 * Callers pass the branch's resolved currency, or `null` when the figures span
 * more than one (see `screenCurrency`). There is deliberately no default: a
 * report silently denominated in riyals is worse than one that fails loudly.
 */
export function useFormat(currency: string | null) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  return useMemo(() => {
    const dash = '—';
    const num = (value: SemanticValue, digits = 0) =>
      value === null || value === undefined
        ? dash
        : new Intl.NumberFormat(locale, {
            maximumFractionDigits: digits,
            minimumFractionDigits: 0,
          }).format(Number(value));

    return {
      /** True when `money()` is rendering unlabelled figures — see `screenCurrency`. */
      mixed: currency === null,
      /**
       * Money crosses the wire as a decimal string and is handed to
       * `formatCurrency` as one.
       *
       * D-03 — this used to hardcode `maximumFractionDigits: 2`, which quietly
       * dropped a fil off every KWD, BHD, OMR and JOD figure (`KWD 12.345` →
       * `KWD 12.35`). `@/lib/i18n` already reads the ISO minor unit from
       * `decimalsFor`, so the only correct implementation is to delegate rather
       * than re-derive the digit count here.
       *
       * D-02 — a null currency means the figure spans several, so it is rendered
       * as a number with an explicit tag instead of a wrong currency label.
       */
      money: (value: SemanticValue) => {
        if (value === null || value === undefined) return dash;
        if (currency === null) {
          return `${num(value, 2)} ${t('reports.currency.mixedTag')}`;
        }
        return formatCurrency(typeof value === 'number' ? value : String(value), currency, locale);
      },
      number: num,
      percent: (value: SemanticValue, digits = 1) =>
        value === null || value === undefined
          ? dash
          : new Intl.NumberFormat(locale, {
              style: 'percent',
              maximumFractionDigits: digits,
            }).format(Number(value)),
      /** A null duration means "never measured", which is not the same as zero. */
      duration: (seconds: SemanticValue) => {
        if (seconds === null || seconds === undefined) return dash;
        const total = Math.max(0, Math.round(Number(seconds)));
        if (total < MINUTE) return t('reports.units.seconds', { n: total });
        if (total < HOUR) {
          return t('reports.units.minutesSeconds', {
            m: Math.floor(total / MINUTE),
            s: total % MINUTE,
          });
        }
        return t('reports.units.hoursMinutes', {
          h: Math.floor(total / HOUR),
          m: Math.floor((total % HOUR) / MINUTE),
        });
      },
      day: (value: SemanticValue) =>
        value === null
          ? dash
          : new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
              new Date(`${value}T00:00:00`),
            ),
      hour: (value: SemanticValue) =>
        value === null ? dash : `${num(value)}${t('reports.units.hourSuffix')}`,
    };
  }, [locale, currency, t]);
}

// ------------------------------------------------------------- chart palette

/**
 * Chart colours come from `@/lib/ui-tokens`, never from a hex invented here,
 * so a series on this screen matches the same entity everywhere else.
 *
 * The order is fixed and never cycled: with three series the adjacent pairs
 * clear the colour-vision separation threshold, and every series is also
 * direct-labelled or listed in the table beside it, so identity never rests on
 * colour alone.
 */
export const seriesColors = [colors.brand[600], colors.info, colors.success] as const;

/** Magnitude is one hue, light to dark - a heatmap is never a rainbow. */
export const heatRamp = [
  colors.brand[50],
  colors.brand[200],
  colors.brand[400],
  colors.brand[600],
  colors.brand[800],
] as const;

export function heatColor(intensity: number | null): string {
  if (intensity === null || Number.isNaN(intensity)) return colors.neutral[200];
  const clamped = Math.min(1, Math.max(0, intensity));
  const index = Math.min(heatRamp.length - 1, Math.floor(clamped * heatRamp.length));
  return heatRamp[index];
}

/** Recessive axes and grid, themed through the same CSS variables as the shell. */
export const axisStyle = {
  tick: { fill: 'var(--muted-foreground)', fontSize: 12 },
  grid: 'var(--border)',
} as const;

// ------------------------------------------------------------------- ranges

export type RangePreset = 'today' | 'week' | 'month' | 'quarter';

export const RANGE_PRESETS: Array<{ key: RangePreset; days: number }> = [
  { key: 'today', days: 0 },
  { key: 'week', days: 6 },
  { key: 'month', days: 29 },
  { key: 'quarter', days: 89 },
];

export function toDayInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export function presetRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 86_400_000);
  return { from: toDayInput(from), to: toDayInput(to) };
}

/** A day input is a local calendar day; widen it to cover the whole day. */
export function rangeToIso(from: string, to: string): { from: string; to: string } {
  return {
    from: new Date(`${from}T00:00:00`).toISOString(),
    to: new Date(`${to}T23:59:59.999`).toISOString(),
  };
}

/** Under two days a daily series is one bar, so switch the grain to hours. */
export function grainFor(from: string, to: string): 'date' | 'hour' {
  const spanDays = (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000;
  return spanDays <= 2 ? 'hour' : 'date';
}

// ------------------------------------------------------------ meta catalogue

/**
 * C-11 — `GET /analytics/meta` is the registry the server compiles queries from.
 *
 * The panels above are fixed reports and are allowed to name their measures.
 * Anything the *operator* composes must not: a measure list typed into the
 * client drifts the moment someone adds a registry entry (or removes one), and
 * the failure mode is a 400 at run time rather than a missing option. So the
 * builder offers exactly what this endpoint publishes, including which
 * dimensions each measure can actually be grouped by — the server rejects a
 * mismatched pair with `Dimension "x" is not available for measure "y"`, and
 * the picker exists so that request is never sent.
 */
export type MeasureFormat = 'money' | 'integer' | 'decimal' | 'seconds' | 'ratio';
export type TemporalGrain = 'date' | 'hour' | 'dayOfWeek';

export interface MeasureMeta {
  name: string;
  /** The fact table it aggregates. Measures from different sources still
   *  combine — the server runs one query per source and joins on the shared
   *  dimensions — but only over dimensions all of them can reach. */
  source: string;
  format: MeasureFormat;
  description: string;
  /** The dimensions this measure can be grouped by, already resolved server-side. */
  dimensions: string[];
  /**
   * ADR-0013 — the permission keys reading this measure costs. Absent means
   * `reports.view` alone; `SemanticQueryService.catalogue()` always fills it in.
   */
  requires: string[];
  /**
   * Whether THIS caller — the access `GET /analytics/meta` was requested
   * under — may actually select this measure. A measure a principal may not
   * read is not filtered out of the list: it is shown greyed out, so an
   * operator who lacks `reports.view_cost` sees that "Gross margin" exists and
   * why they cannot pick it, rather than a list that quietly shrinks with no
   * explanation. Naming it anyway in a request still gets a 403 — this flag is
   * what stops the client from sending that request in the first place.
   */
  permitted: boolean;
}

export interface DimensionMeta {
  name: string;
  valueType: 'string' | 'number';
  description: string;
  temporal: TemporalGrain | null;
  sources: string[];
  /** As `MeasureMeta.requires` — none declare one today, but the field always arrives. */
  requires: string[];
  /** As `MeasureMeta.permitted`. */
  permitted: boolean;
}

export interface AnalyticsMeta {
  measures: MeasureMeta[];
  dimensions: DimensionMeta[];
  limits: { defaultRows: number; maxRows: number };
}

export function useAnalyticsMeta() {
  return useApi<AnalyticsMeta>('/analytics/meta');
}

/** Caps the DTO enforces (`ArrayMaxSize`); over them the request is a 400. */
export const MAX_MEASURES = 20;
export const MAX_DIMENSIONS = 4;

/**
 * The dimensions every selected measure supports. Intersecting rather than
 * unioning is the whole point: a dimension one source cannot reach would make
 * the server refuse the query, so an unreachable option is never offered.
 */
export function sharedDimensions(measures: MeasureMeta[]): string[] {
  if (!measures.length) return [];
  return measures
    .slice(1)
    .reduce<string[]>(
      (allowed, measure) => allowed.filter((name) => measure.dimensions.includes(name)),
      [...measures[0].dimensions],
    );
}

/**
 * Renders a measure by the format the *server* declared for it, so a new
 * measure arrives correctly formatted without a client-side lookup table to
 * forget to update. `useFormat` already owns the currency and locale rules.
 */
export function measureFormatter(
  format: ReturnType<typeof useFormat>,
): (value: SemanticValue, measure: MeasureMeta | undefined) => string {
  return (value, measure) => {
    switch (measure?.format) {
      case 'money':
        return format.money(value);
      case 'seconds':
        return format.duration(value);
      case 'ratio':
        return format.percent(value);
      case 'decimal':
        return format.number(value, 2);
      case 'integer':
        return format.number(value, 0);
      default:
        // An unknown format means the server grew one we do not model yet.
        // Printing the raw value is honest; guessing "money" would not be.
        return value === null || value === undefined ? '—' : String(value);
    }
  };
}

/**
 * A dimension cell. The semantic layer emits `<name>` (the stable key) and,
 * where the source can reach one, `<name>Label` — so the id is the fallback,
 * never the thing shown when a name exists. Temporal dimensions carry no label
 * and are formatted from their grain instead.
 */
export function dimensionCell(
  row: SemanticRow,
  dimension: DimensionMeta | undefined,
  format: ReturnType<typeof useFormat>,
  weekday: (value: SemanticValue) => string,
): string {
  if (!dimension) return '';
  const raw = row[dimension.name] ?? null;
  if (dimension.temporal === 'date') return format.day(raw);
  if (dimension.temporal === 'hour') return format.hour(raw);
  if (dimension.temporal === 'dayOfWeek') return weekday(raw);
  const label = row[`${dimension.name}Label`];
  return String(label ?? raw ?? '—');
}

/** `dayOfWeek` is 0 = Sunday; a locale name beats a bare digit. */
export function useWeekdayNames() {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2024-01-07 was a Sunday, so index 0 lines up with the server's convention.
    const names = Array.from({ length: 7 }, (_, index) =>
      fmt.format(new Date(Date.UTC(2024, 0, 7 + index))),
    );
    return (value: SemanticValue) =>
      value === null || value === undefined ? '—' : (names[Number(value)] ?? String(value));
  }, [locale]);
}
